import { isWithCode } from '@orionprotocol/utils';
import { JsonRpcSigner } from 'ethers';
import { walletConnect, WalletConnectParameters } from '@wagmi/connectors';
import {
  connect,
  createConfig,
  disconnect,
  getConnections,
  getConnectors,
  reconnect,
  switchChain,
  watchAccount,
  watchChainId,
} from '@wagmi/core';

import { Logger } from '../Logger';
import { ConnectSuccessData, GetWalletAPI } from '../WalletAPI';
import { getEthersSigner } from '../ethersAdapters';
import {
  WalletAPIError,
  WalletChangeNetworkFailed,
  WalletConnectCancelled,
  WalletDisconnectFailed,
} from '../walletAPIErrors';
import { availableChains, transports } from '../availableChains';

interface WalletConnectSession {
  peer?: {
    metadata?: {
      name?: string;
    };
  };
}

export function createWalletConnectAPI({
  logger = console,
  projectId,
  qrModalOptions,
}: {
  logger?: Logger;
  projectId: string;
  qrModalOptions?: WalletConnectParameters['qrModalOptions'];
}): GetWalletAPI {
  const config = createConfig({
    chains: availableChains,
    connectors: [walletConnect({ projectId })],
    transports,
  });

  return ({ type, onAccountChanged, onChainChanged, onDisconnected }) => {
    const unwatchAccounts = watchAccount(config, {
      onChange(data) {
        onAccountChanged?.({
          address: data.address,
          chainId: data.chainId,
        });
      },
    });

    const unwatchChainId = watchChainId(config, {
      onChange(chainId) {
        onChainChanged?.({
          chainId,
        });
      },
    });

    return {
      type,
      getSigner: (): Promise<JsonRpcSigner | undefined> =>
        getEthersSigner(config),
      connect: async (): Promise<ConnectSuccessData> => {
        try {
          const connections = getConnections(config);
          if (connections.length !== 0) {
            const reconnectData = await reconnect(config);
            return {
              address: reconnectData?.[0]?.accounts[0].toLowerCase(),
              chainId: reconnectData?.[0]?.chainId,
            };
          }

          const { accounts, chainId } = await connect(config, {
            connector: walletConnect({ projectId, qrModalOptions }),
          });
          return {
            address: accounts[0].toLowerCase(),
            chainId,
          };
        } catch (e) {
          const code = isWithCode(e) ? e.code : undefined;
          if (code === 4001) {
            throw new WalletConnectCancelled(e, logger);
          }

          throw new WalletAPIError(e, logger);
        }
      },
      disconnect: async (): Promise<void> => {
        try {
          await disconnect(config);

          unwatchAccounts();
          unwatchChainId();

          // NOTE: this section if disconnect not success
          const connectors = getConnectors(config);
          const walletConnectConnector = connectors.find(
            (connector) => connector.name === 'WalletConnect',
          );
          if (
            walletConnectConnector &&
            walletConnectConnector.disconnect !== undefined
          ) {
            await walletConnectConnector.disconnect();
          }

          onDisconnected?.();
        } catch (e) {
          throw new WalletDisconnectFailed(e, logger);
        }
      },
      changeNetwork: async ({
        chainId,
      }: {
        chainId: string;
      }): Promise<void> => {
        try {
          await switchChain(config, {
            chainId: Number(chainId),
          });
        } catch (e) {
          throw new WalletChangeNetworkFailed(e, logger);
        }
      },
      getWalletConnectWalletName: async (): Promise<string | undefined> => {
        const connectors = getConnectors(config);
        try {
          const walletConnectConnector = connectors.find(
            (connector) => connector.name === 'WalletConnect',
          );
          if (!walletConnectConnector) {
            logger.error('WalletConnect connector not found.');
            return undefined;
          }

          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const provider = (await walletConnectConnector.getProvider()) as {
            session: WalletConnectSession;
          };

          const walletName = provider.session?.peer?.metadata?.name;

          return walletName;
        } catch (e) {
          logger.error('Failed to retrieve WalletConnect wallet name.', e);
          return undefined;
        }
      },
    };
  };
}
